import { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
    Image, Plus, Trash2, Edit, X, Save, ExternalLink, Loader2, Upload,
    Eye, MousePointer, ToggleLeft, ToggleRight, Building2, User, Calendar,
    AlertTriangle, ShieldAlert
} from 'lucide-react';
import { getMyAdvertisements, createAdvertisement, updateAdvertisement, deleteAdvertisement } from '../../services/advertisementsService';
import { getUserPlaces } from '../../services/userPlacesService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import { uploadFile } from '../../services/filesService';
import { checkImageContent, isModerationAvailable } from '../../services/contentModerationService';
import PageHeader from '../ui/PageHeader';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';

const AdvertisementsPage = () => {
    const { t } = useTranslation();
    const user = getUser();

    // State
    const [advertisements, setAdvertisements] = useState([]);
    const [places, setPlaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingAd, setEditingAd] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        imagePath: '',
        linkUrl: '',
        displayType: 'profile',
        placeUuid: '',
        scheduleType: 'always', // 'always', 'scheduled', 'disabled'
        startDate: '',
        endDate: ''
    });

    const [previewImage, setPreviewImage] = useState(null);
    const [moderationError, setModerationError] = useState(null);
    const [checkingContent, setCheckingContent] = useState(false);

    // Load data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [adsData, placesData] = await Promise.all([
                getMyAdvertisements(),
                getUserPlaces()
            ]);
            setAdvertisements(adsData);
            setPlaces(placesData.filter(p => p.isPublic));
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            imagePath: '',
            linkUrl: '',
            displayType: 'profile',
            placeUuid: '',
            scheduleType: 'always',
            startDate: '',
            endDate: ''
        });
        setPreviewImage(null);
        setEditingAd(null);
    };

    const handleOpenForm = (ad = null) => {
        if (ad) {
            setEditingAd(ad);
            // Determine scheduleType based on existing data
            let scheduleType = 'always';
            if (ad.isActive === false) {
                scheduleType = 'disabled';
            } else if (ad.startDate || ad.endDate) {
                scheduleType = 'scheduled';
            }
            setFormData({
                title: ad.title || '',
                description: ad.description || '',
                imagePath: ad.imagePath || '',
                linkUrl: ad.linkUrl || '',
                displayType: ad.displayType || 'profile',
                placeUuid: ad.placeUuid || '',
                scheduleType: scheduleType,
                startDate: ad.startDate ? ad.startDate.split('T')[0] : '',
                endDate: ad.endDate ? ad.endDate.split('T')[0] : ''
            });
            setPreviewImage(ad.imagePath ? `${FILES_BASE_URL}/${ad.imagePath}` : null);
        } else {
            resetForm();
        }
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        resetForm();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingImage(true);
        setModerationError(null);

        try {
            // Check content moderation first
            if (isModerationAvailable()) {
                setCheckingContent(true);
                const moderationResult = await checkImageContent(file);
                setCheckingContent(false);

                if (!moderationResult.safe) {
                    setModerationError(moderationResult.reason || 'Cette image contient du contenu inapproprié et ne peut pas être utilisée.');
                    setUploadingImage(false);
                    return;
                }
            }

            const userUuid = user.peopleUuid || user.peopleuuid || user.PEOPLEUUID;
            const result = await uploadFile(file, {
                path: 'advertisements/',
                fileType: 'advertisement_image',
                entityType: 'advertisements',
                entityUuid: userUuid,
                uploadedById: user.id || user.ID
            });

            const success = result.success ?? result.SUCCESS;
            const data = result.data ?? result.DATA ?? {};
            const filePath = data.filePath ?? data.FILEPATH ?? data.filepath ?? result.filePath ?? result.FILEPATH;
            console.log('Upload result:', { success, filePath, data });
            if (success && filePath) {
                setFormData(prev => ({ ...prev, imagePath: filePath }));
                setPreviewImage(URL.createObjectURL(file));
            }
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setUploadingImage(false);
            setCheckingContent(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert(t.advertisements?.titleRequired || 'Le titre est requis');
            return;
        }
        if (!formData.imagePath) {
            alert(t.advertisements?.imageRequired || 'Une image est requise');
            return;
        }

        setSaving(true);
        try {
            // Prepare data with isActive based on scheduleType
            const dataToSave = {
                ...formData,
                isActive: formData.scheduleType !== 'disabled',
                // Clear dates if not scheduled
                startDate: formData.scheduleType === 'scheduled' ? formData.startDate : null,
                endDate: formData.scheduleType === 'scheduled' ? formData.endDate : null
            };
            delete dataToSave.scheduleType; // Don't send scheduleType to API

            let result;
            if (editingAd) {
                result = await updateAdvertisement(editingAd.id, dataToSave);
            } else {
                result = await createAdvertisement(dataToSave);
            }

            if (result.success) {
                await loadData();
                handleCloseForm();
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error saving advertisement:', error);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;

        try {
            const result = await deleteAdvertisement(deleteConfirm.id);
            if (result.success) {
                await loadData();
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error deleting advertisement:', error);
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleToggleActive = async (ad) => {
        try {
            await updateAdvertisement(ad.id, { isActive: !ad.isActive });
            await loadData();
        } catch (error) {
            console.error('Error toggling advertisement:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <PageHeader
                icon={Image}
                title={t.advertisements?.title || 'Mes publicités'}
                subtitle={t.advertisements?.subtitle || 'Gérez vos publicités pour votre profil et vos établissements'}
            />

            {/* Add button */}
            <div className="mb-6">
                <Button onClick={() => handleOpenForm()} icon={Plus}>
                    {t.advertisements?.addNew || 'Nouvelle publicité'}
                </Button>
            </div>

            {/* Advertisements list */}
            {advertisements.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t.advertisements?.noAds || 'Aucune publicité'}
                    </h3>
                    <p className="text-gray-500 mb-4">
                        {t.advertisements?.noAdsDesc || 'Créez votre première publicité pour la promouvoir sur votre profil ou vos établissements'}
                    </p>
                    <Button onClick={() => handleOpenForm()} icon={Plus}>
                        {t.advertisements?.createFirst || 'Créer ma première publicité'}
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {advertisements.map(ad => (
                        <div key={ad.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                            {/* Main content - horizontal layout */}
                            <div className="flex flex-col sm:flex-row">
                                {/* Image */}
                                <div className="relative w-full sm:w-48 h-40 sm:h-auto sm:min-h-[160px] bg-gray-100 flex-shrink-0">
                                    {ad.imagePath ? (
                                        <img
                                            src={`${FILES_BASE_URL}/${ad.imagePath}`}
                                            alt={ad.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <Image className="w-12 h-12 text-gray-300" />
                                        </div>
                                    )}
                                    {/* Status badge */}
                                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                                        ad.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {ad.isActive ? (t.advertisements?.active || 'Active') : (t.advertisements?.inactive || 'Inactive')}
                                    </div>
                                    {/* Display type badge */}
                                    <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium bg-cabaza-purple/90 text-white flex items-center gap-1">
                                        {ad.displayType === 'profile' ? (
                                            <>
                                                <User className="w-3 h-3" />
                                                Profil
                                            </>
                                        ) : (
                                            <>
                                                <Building2 className="w-3 h-3" />
                                                {ad.placeName || 'Établissement'}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 p-4 flex flex-col">
                                    <h3 className="font-semibold text-gray-900 text-lg mb-1">{ad.title}</h3>
                                    {ad.description && (
                                        <p className="text-sm text-gray-500 flex-1">{ad.description}</p>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50">
                                {/* Actions - left */}
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => handleToggleActive(ad)}
                                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                                        title={ad.isActive ? 'Désactiver' : 'Activer'}
                                    >
                                        {ad.isActive ? (
                                            <ToggleRight className="w-5 h-5 text-green-600" />
                                        ) : (
                                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleOpenForm(ad)}
                                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                                        title="Modifier"
                                    >
                                        <Edit className="w-5 h-5 text-gray-600" />
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirm(ad)}
                                        className="p-2 rounded-lg hover:bg-red-100 transition-colors"
                                        title="Supprimer"
                                    >
                                        <Trash2 className="w-5 h-5 text-red-500" />
                                    </button>
                                </div>

                                {/* Link - center */}
                                <div className="flex-1 flex justify-center">
                                    {ad.linkUrl && (
                                        <a
                                            href={ad.linkUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-cabaza-purple hover:underline flex items-center gap-1"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            {t.advertisements?.viewLink || 'Voir le lien'}
                                        </a>
                                    )}
                                </div>

                                {/* Stats - right */}
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                        <Eye className="w-4 h-4" />
                                        {ad.viewCount || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <MousePointer className="w-4 h-4" />
                                        {ad.clickCount || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Form Modal */}
            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                title={editingAd ? (t.advertisements?.editAd || 'Modifier la publicité') : (t.advertisements?.newAd || 'Nouvelle publicité')}
                size="lg"
                onSubmit={handleSubmit}
                submitText={saving ? (t.common?.saving || 'Enregistrement...') : (t.common?.save || 'Enregistrer')}
                loading={saving}
            >
                <div className="space-y-4">
                    {/* Content policy warning */}
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-800">
                                {t.advertisements?.contentPolicy || 'Politique de contenu'}
                            </p>
                            <p className="text-sm text-amber-700 mt-1">
                                {t.advertisements?.contentPolicyDesc || 'Les contenus pour adultes, pornographiques, violents ou haineux sont strictement interdits. Les images sont automatiquement vérifiées par notre système de modération IA. Tout contenu inapproprié sera rejeté et peut entraîner la suspension de votre compte.'}
                            </p>
                        </div>
                    </div>

                    {/* Moderation error */}
                    {moderationError && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-red-800">
                                    {t.advertisements?.contentRejected || 'Contenu rejeté'}
                                </p>
                                <p className="text-sm text-red-700 mt-1">{moderationError}</p>
                            </div>
                        </div>
                    )}

                    {/* Image upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.advertisements?.image || 'Image'} *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-cabaza-purple transition-colors">
                            {previewImage ? (
                                <div className="relative">
                                    <img
                                        src={previewImage}
                                        alt="Preview"
                                        className="max-h-48 mx-auto rounded-lg"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPreviewImage(null);
                                            setFormData(prev => ({ ...prev, imagePath: '' }));
                                        }}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        disabled={uploadingImage}
                                    />
                                    {uploadingImage || checkingContent ? (
                                        <div className="text-center">
                                            <Loader2 className="w-8 h-8 mx-auto text-cabaza-purple animate-spin" />
                                            {checkingContent && (
                                                <p className="text-sm text-gray-500 mt-2">
                                                    {t.advertisements?.checkingContent || 'Vérification du contenu...'}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                            <p className="text-sm text-gray-500">
                                                {t.advertisements?.uploadImage || 'Cliquez pour télécharger une image'}
                                            </p>
                                        </>
                                    )}
                                </label>
                            )}
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.advertisements?.adTitle || 'Titre'} *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={t.advertisements?.titlePlaceholder || 'Titre de votre publicité'}
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.advertisements?.adDescription || 'Description'}
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            rows={3}
                            placeholder={t.advertisements?.descriptionPlaceholder || 'Description optionnelle'}
                        />
                    </div>

                    {/* Link URL */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.advertisements?.linkUrl || 'Lien URL'}
                        </label>
                        <input
                            type="url"
                            value={formData.linkUrl}
                            onChange={(e) => setFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder="https://..."
                        />
                    </div>

                    {/* Display type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.advertisements?.displayOn || 'Afficher sur'}
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="displayType"
                                    value="profile"
                                    checked={formData.displayType === 'profile'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, displayType: e.target.value, placeUuid: '' }))}
                                    className="text-cabaza-purple focus:ring-cabaza-purple"
                                />
                                <User className="w-4 h-4" />
                                {t.advertisements?.myProfile || 'Mon profil'}
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="displayType"
                                    value="place"
                                    checked={formData.displayType === 'place'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, displayType: e.target.value }))}
                                    className="text-cabaza-purple focus:ring-cabaza-purple"
                                />
                                <Building2 className="w-4 h-4" />
                                {t.advertisements?.myPlace || 'Mon établissement'}
                            </label>
                        </div>
                    </div>

                    {/* Place selector (if displayType is 'place') */}
                    {formData.displayType === 'place' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t.advertisements?.selectPlace || 'Sélectionnez un établissement'}
                            </label>
                            {places.length > 0 ? (
                                <select
                                    value={formData.placeUuid}
                                    onChange={(e) => setFormData(prev => ({ ...prev, placeUuid: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                    required={formData.displayType === 'place'}
                                >
                                    <option value="">{t.advertisements?.choosePlaceHolder || '-- Choisir --'}</option>
                                    {places.map(place => (
                                        <option key={place.placeUuid || place.localUuid} value={place.placeUuid || place.localUuid}>
                                            {place.placeName || place.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-sm text-gray-500 italic">
                                    {t.advertisements?.noPublicPlaces || 'Vous devez d\'abord rendre un établissement public'}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Schedule type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {t.advertisements?.scheduleType || 'Planification'}
                        </label>
                        <div className="flex flex-wrap gap-3">
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                                formData.scheduleType === 'always'
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}>
                                <input
                                    type="radio"
                                    name="scheduleType"
                                    value="always"
                                    checked={formData.scheduleType === 'always'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleType: e.target.value }))}
                                    className="hidden"
                                />
                                <ToggleRight className="w-4 h-4" />
                                {t.advertisements?.alwaysActive || 'Toujours active'}
                            </label>
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                                formData.scheduleType === 'scheduled'
                                    ? 'border-cabaza-purple bg-purple-50 text-cabaza-purple'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}>
                                <input
                                    type="radio"
                                    name="scheduleType"
                                    value="scheduled"
                                    checked={formData.scheduleType === 'scheduled'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleType: e.target.value }))}
                                    className="hidden"
                                />
                                <Calendar className="w-4 h-4" />
                                {t.advertisements?.scheduled || 'Selon les dates'}
                            </label>
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                                formData.scheduleType === 'disabled'
                                    ? 'border-gray-500 bg-gray-100 text-gray-700'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}>
                                <input
                                    type="radio"
                                    name="scheduleType"
                                    value="disabled"
                                    checked={formData.scheduleType === 'disabled'}
                                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleType: e.target.value }))}
                                    className="hidden"
                                />
                                <ToggleLeft className="w-4 h-4" />
                                {t.advertisements?.disabled || 'Désactivée'}
                            </label>
                        </div>
                    </div>

                    {/* Date range (only if scheduled) */}
                    {formData.scheduleType === 'scheduled' && (
                        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t.advertisements?.startDate || 'Date de début'}
                                </label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t.advertisements?.endDate || 'Date de fin'}
                                </label>
                                <input
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent bg-white"
                                />
                            </div>
                        </div>
                    )}

                </div>
            </Modal>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={handleDelete}
                title={t.advertisements?.deleteTitle || 'Supprimer la publicité'}
                message={t.advertisements?.deleteMessage || 'Êtes-vous sûr de vouloir supprimer cette publicité ? Cette action est irréversible.'}
                confirmText={t.common?.delete || 'Supprimer'}
                cancelText={t.common?.cancel || 'Annuler'}
                variant="danger"
            />
        </div>
    );
};

export default AdvertisementsPage;
